import type { StoryType } from "@config";
import type { GenerateOutlineResult } from "./types/generation";
import type { Slide } from "./components/sortable-slide";
import type { TemplateSlideMapping } from "./utils/template-slide-mappings";
import { useLocation } from "./funnel-navigation";

/**
 * The funnel's in-memory step paths. They deliberately keep the legacy
 * `/pages/wizard` prefix: the real browser route hosting the signup funnel is
 * `wizard/*`, and deep links seed the in-memory history from the browser path.
 */
export const FUNNEL_PATHS = {
  root: "/pages/wizard",
  templates: "/pages/wizard/templates",
  aiType: "/pages/wizard/ai/type",
  aiBrand: "/pages/wizard/ai/brand",
  aiDescribe: "/pages/wizard/ai/describe",
  aiGenerating: "/pages/wizard/ai/generating",
  aiReady: "/pages/wizard/ai/ready",
} as const;

export type FunnelPath = (typeof FUNNEL_PATHS)[keyof typeof FUNNEL_PATHS];

/**
 * Where a template comes from on the template step: one of the account's own
 * documents, one of its sent versions, Storydoc's own templates, or a set of
 * slides picked in the library.
 */
export type TemplateSource = "mine" | "versions" | "storydoc" | "slides";

/**
 * What each step expects in its in-memory `location.state`. Steps read it via
 * `useStepState` instead of casting `location.state` by hand, so a misspelled
 * key in a `navigate(...)` call becomes a compile error at the reader.
 */
export interface FunnelStepStates {
  [FUNNEL_PATHS.root]: null;
  [FUNNEL_PATHS.templates]: {
    category?: string;
    /** Set when arriving from the brand step, so "Create with AI" on a card doesn't route back through it. */
    brandDone?: boolean;
    /**
     * Which templates to open on: the account's own documents ("mine"), its
     * versions, or Storydoc's. Platform only -- a signup account has none of
     * its own yet.
     */
    source?: TemplateSource;
  } | null;
  [FUNNEL_PATHS.aiType]: { category?: string } | null;
  // Real shape sent by how-to-start.tsx ("Start from blank" passes
  // `{ mode: "blank" }`) and read by start-with-ai.tsx (`funnelState?.mode`).
  [FUNNEL_PATHS.aiBrand]: {
    docType?: string;
    templateId?: string;
    /**
     * Where the picked template's own story JSON lives.
     *
     * Carried alongside the id so the prompt step can read the template's
     * slides and write the outline for them — see
     * utils/template-outline-seed. Fetched there rather than here so picking
     * a template still navigates instantly.
     */
    templateContentPointer?: string;
    mode?: string;
  } | null;
  [FUNNEL_PATHS.aiDescribe]: {
    docType?: string;
    brand?: string;
    templateId?: string;
    /** See aiBrand above. */
    templateContentPointer?: string;
    /**
     * Where the template came from. One of the user's own documents ("mine")
     * or versions may hold pictures that are theirs to keep, so the prompt
     * step offers a choice about them; a Storydoc template's pictures are
     * stand-ins; a set of slides picked in the library ("slides") arrives as
     * `templateStory`.
     */
    templateSource?: TemplateSource;
    /**
     * The template's story itself, when it was assembled in the browser
     * rather than stored anywhere -- slides picked in the library. Read in
     * place of the pointer; see utils/template-source.
     */
    templateStory?: Record<string, unknown>;
    /**
     * The org story the template is (or was cut from). A version written
     * from it hangs off that story; see features/standalone-versions.
     */
    basePageId?: string;
  } | null;
  [FUNNEL_PATHS.aiGenerating]: {
    docType?: string;
    brand?: string;
    templateId?: string;
    outline: Slide[];
    /** Full outline result from /generate-outline — tone, image guidelines, title. */
    outlineMeta: GenerateOutlineResult | null;
    fileUrl?: string | null;
    companyName?: string | null;
    /** The domain of `companyName`; see DeckInputs.companyDomain. */
    companyDomain?: string | null;
    /** Platform: theme picked on the prompt step; null/absent = account default. */
    designSystemId?: string | null;
    /**
     * The picked template's own designs, one per slide — read on the prompt
     * step, where the template's story is already fetched. Absent on every
     * non-template path.
     */
    templateSlideMappings?: TemplateSlideMapping[] | null;
    /** Where the template's story lives, so its slides can be filled directly. */
    templateContentPointer?: string;
    /** Leave the template's pictures as they are; see DeckInputs.keepImages. */
    keepImages?: boolean;
    /** The assembled template story, when there is no pointer; see aiDescribe. */
    templateStory?: Record<string, unknown>;
  } | null;
  // Real shape read by ready.tsx (state?.docType/brand/templateId/template) —
  // generating.tsx also carries `outline` forward but ready.tsx never reads it.
  [FUNNEL_PATHS.aiReady]: {
    docType?: string;
    brand?: string;
    templateId?: string;
    template?: StoryType;
  } | null;
}

/** Typed reader for the current step's navigation state. */
export const useStepState = <P extends keyof FunnelStepStates>(
  _path: P,
): FunnelStepStates[P] | null =>
  (useLocation().state ?? null) as FunnelStepStates[P] | null;
